/**
 * Outbound provisioning — wire shapes and form state.
 *
 * The HTTP contract is api/openapi/admin.yaml (TenantProvisioning*); these
 * schemas validate what comes back rather than redefining it, so every field
 * matches a documented property exactly.
 */

import { z } from "zod";

export const CONNECTION_STATES = ["draft", "backfilling", "active", "paused", "disabled"] as const;
export type ConnectionState = (typeof CONNECTION_STATES)[number];

export const SCOPE_KINDS = ["all_people", "groups"] as const;
export type ScopeKind = (typeof SCOPE_KINDS)[number];

export const DELETE_POLICIES = ["never", "delete"] as const;
export type DeletePolicy = (typeof DELETE_POLICIES)[number];

export const EGRESS_CLASSES = ["open", "acknowledged", "withheld"] as const;
export type EgressClass = (typeof EGRESS_CLASSES)[number];

export const credentialSchema = z.object({
  auth_scheme: z.string(),
  sealed_for_origin: z.string(),
  current: z.boolean(),
  created_at: z.string(),
  created_by: z.string().nullable(),
});

export const mappingSchema = z.object({
  attribute: z.string(),
  key: z.string(),
  target_path: z.string(),
  egress: z.enum(EGRESS_CLASSES),
  acknowledged_by: z.string().nullable(),
  acknowledged_at: z.string().nullable(),
});

export const connectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  state: z.enum(CONNECTION_STATES),
  application: z.object({ id: z.string(), name: z.string().nullable() }).nullable(),
  endpoint_url: z.string().nullable(),
  destination_origin: z.string().nullable(),
  delivery: z.string(),
  scope_kind: z.enum(SCOPE_KINDS),
  delete_policy: z.enum(DELETE_POLICIES),
  /** Whether this connection maintains groups at the target, not only people. */
  push_groups: z.boolean(),
  backfilled_at: z.string().nullable(),
  queued: z.number(),
  deprovision_deadline_minutes: z.number(),
  open_obligations: z.number(),
  overdue_obligations: z.number(),
  armed: z.boolean(),
  failed: z.number(),
  target_hold: z.object({ until: z.string(), failures: z.number(), reason: z.string() }).nullable(),
  /** Deactivations and deletions the connection may send in a trailing day. */
  deprovision_ceiling: z.number(),
  /** Set while the connection has stopped itself at its ceiling. */
  ceiling_tripped: z.object({ at: z.string(), sent: z.number() }).nullable(),
  rearm_required: z.boolean(),
  credential: credentialSchema.nullable(),
  groups: z.array(z.object({ id: z.string(), display_name: z.string() })),
  attributes: z.array(mappingSchema),
  missing: z.array(z.string()),
  created_at: z.string(),
  updated_at: z.string(),
});

export const connectionListSchema = z.object({
  items: z.array(connectionSchema),
  delivery_enabled: z.boolean(),
  /** False while the tenant's Directory area is off: nothing new is started at any target. */
  directory_area_on: z.boolean(),
});
export type ConnectionList = z.infer<typeof connectionListSchema>;

const previewPeople = z.array(z.object({ person_id: z.string(), person_name: z.string().nullable() }));
const previewCategories = <T extends z.ZodTypeAny>(t: T) => z.object({ provision: t, update: t, deactivate: t, delete: t });
export const armingPreviewSchema = z.object({
  epoch: z.number(),
  armed_epoch: z.number().nullable(),
  armed: z.boolean(),
  queued: z.number(),
  counts: previewCategories(z.number()),
  samples: previewCategories(previewPeople),
  digest: z.string(),
});
export type ArmingPreview = z.infer<typeof armingPreviewSchema>;

export const failedDeliverySchema = z.object({
  id: z.string(),
  person_id: z.string().nullable(),
  person_name: z.string().nullable(),
  reasons: z.array(z.string()),
  attempts: z.number(),
  last_error: z.string().nullable(),
  failed_at: z.string(),
});
export const failedDeliveriesSchema = z.object({ items: z.array(failedDeliverySchema) });
export type FailedDelivery = z.infer<typeof failedDeliverySchema>;

export const targetPathsSchema = z.object({
  paths: z.array(z.object({ path: z.string(), suggested_attribute: z.string(), required: z.boolean() })),
  attributes: z.array(z.object({ attribute: z.string(), egress: z.enum(EGRESS_CLASSES), container: z.boolean() })),
});

export const probeResultSchema = z.object({
  ok: z.boolean(),
  status: z.number().nullable(),
  duration_ms: z.number(),
  detail: z.string().nullable(),
});

export const deliverySchema = z.object({
  id: z.string(),
  person_id: z.string().nullable(),
  person_name: z.string().nullable(),
  /** Set instead of the person pair when the exchange was about a group. */
  group_id: z.string().nullable(),
  group_name: z.string().nullable(),
  operation: z.enum(["probe", "create", "link", "update", "deactivate", "delete"]),
  attributes: z.array(z.string()),
  http_status: z.number().nullable(),
  outcome: z.enum(["success", "retry", "refused"]),
  duration_ms: z.number(),
  error: z.string().nullable(),
  recorded_at: z.string(),
});
export const deliveriesSchema = z.object({ items: z.array(deliverySchema) });
export type Delivery = z.infer<typeof deliverySchema>;

export const obligationSchema = z.object({
  id: z.string(),
  person_id: z.string(),
  person_name: z.string().nullable(),
  kind: z.enum(["deactivate", "delete"]),
  state: z.enum(["open", "verified", "unverified_2xx", "withdrawn"]),
  opened_at: z.string(),
  deadline_at: z.string(),
  expired_at: z.string().nullable(),
  attempts: z.number(),
  last_attempt_at: z.string().nullable(),
  last_status: z.number().nullable(),
  last_error: z.string().nullable(),
  closed_at: z.string().nullable(),
});
export const obligationsSchema = z.object({ items: z.array(obligationSchema) });
export type Obligation = z.infer<typeof obligationSchema>;

/** The deadlines offered, in minutes. */
export const DEADLINE_OPTIONS: ReadonlyArray<{ minutes: number; label: string }> = [
  { minutes: 15, label: "15 minutes" },
  { minutes: 60, label: "1 hour" },
  { minutes: 240, label: "4 hours" },
  { minutes: 1440, label: "1 day" },
  { minutes: 4320, label: "3 days" },
];

export const operationLabel: Record<Delivery["operation"], string> = {
  probe: "Tested",
  create: "Created",
  link: "Matched",
  update: "Updated",
  deactivate: "Deactivated",
  delete: "Deleted",
};

export type Connection = z.infer<typeof connectionSchema>;
export type Mapping = z.infer<typeof mappingSchema>;
export type TargetPaths = z.infer<typeof targetPathsSchema>;
export type ProbeResult = z.infer<typeof probeResultSchema>;

// ── Forms ────────────────────────────────────────────────────────────────

export const newConnectionFormSchema = z.object({
  name: z.string().trim().min(1, "Name the connection").max(100, "At most 100 characters"),
  endpoint_url: z
    .string()
    .trim()
    .refine((v) => v === "" || v.startsWith("https://"), "The endpoint must start with https://"),
  scope_kind: z.enum(SCOPE_KINDS),
});
export type NewConnectionForm = z.infer<typeof newConnectionFormSchema>;

export const credentialFormSchema = z.object({
  secret: z.string().min(1, "Paste the target's token").max(8192, "That is longer than a token can be"),
  auth_scheme: z
    .string()
    .trim()
    .regex(/^[A-Za-z][A-Za-z0-9-]{0,31}$/, "One word, such as Bearer"),
});
export type CredentialForm = z.infer<typeof credentialFormSchema>;

/** One editable row of the attribute map. */
export interface MappingDraft {
  attribute: string;
  key: string;
  target_path: string;
  acknowledge: boolean;
}

// ── Labels ───────────────────────────────────────────────────────────────

export const stateLabel: Record<ConnectionState, string> = {
  draft: "Draft",
  backfilling: "Starting",
  active: "Active",
  paused: "Paused",
  disabled: "Disabled",
};

export const stateVariant: Record<ConnectionState, "secondary" | "info" | "success" | "warning" | "outline"> = {
  draft: "secondary",
  backfilling: "info",
  active: "success",
  paused: "warning",
  disabled: "outline",
};

export const scopeLabel: Record<ScopeKind, string> = {
  all_people: "Everyone active",
  groups: "Selected groups",
};

/** Attribute names as an administrator reads them. */
export function attributeLabel(name: string): string {
  const special: Record<string, string> = {
    ad_dn: "AD distinguished name",
    ad_object_sid: "AD object SID",
    ad_object_guid: "AD object GUID",
    ad_user_principal_name: "AD user principal name",
    profile_url: "Profile URL",
    ent_organization: "Organization",
  };
  if (special[name]) return special[name];
  const words = name.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}
